import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { AlertCircle, User01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Avatar } from "@/components/base/avatar/avatar";
import { AvatarLabelGroup } from "@/components/base/avatar/avatar-label-group";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { EmptyValue } from "@/components/common";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import {
	type OrganizationUserInfo,
	organizationQueryOptions,
} from "@/services/organization";
import { getUserInitials } from "@/utils/get-user-initials";
import { SearchSchema } from "../$organizationId";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId/users",
)({
	component: UsersTabComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function UsersTabComponent() {
	const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
	const { t } = useAppTranslation();
	// const queryErrorResetBoundary = useQueryErrorResetBoundary();
	// const router = useRouter();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId/users",
	});

	const pageSize = 50;

	// Get user count from parent's query (shared cache - no extra network requests!)
	const orgUserCount = useQuery({
		...organizationQueryOptions.users.count(params.organizationId),
		retry: 1, // Fail fast
	});

	// This tab NEEDS the users list - use the reusable infinite query helper
	const {
		data,
		error,
		isFetching,
		isLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		...organizationQueryOptions.users.infinite(
			params.organizationId,
			{
				OrderBy: {
					Fields: ["User.LastName", "User.FirstName"],
					Descending: false,
				},
			}, // filters with OrderBy
			pageSize,
			orgUserCount.data, // Pass total count for accurate pagination
		),
		retry: 1, // Override default retry behavior to fail fast
	});

	// Check if THIS TAB's data has errors
	// Only show error state if the main users query fails, not the count
	const hasError = !!error;

	// 3) Flatten pages for the table
	const users = useMemo(
		() => (data?.pages.flat() ?? []) as OrganizationUserInfo[],
		[data],
	);

	// Capture scroll container for virtualizer and IntersectionObserver
	const rowCount = hasNextPage ? users.length + 1 : users.length;

	// Create virtualizer with optimized settings
	const virtualizer = useVirtualizer({
		count: rowCount,
		getScrollElement: () => rootEl,
		estimateSize: () => 72, // Adjust based on your actual row height
		overscan: 10, //Render 10 extra rows outside the visible area for smooth scrolling
	});

	// Use react-intersection-observer to detect when to load more
	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0, //Fire when any part (even 1 pixel) of the element enters the viewport
		rootMargin: "0px 0px 900px 0px",
		root: rootEl ?? undefined,
	});

	// Fetch next page when sentinel comes into view
	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	// Avatar cell component - extracted to properly use hooks
	function UserAvatarCell({ user }: { user: OrganizationUserInfo }) {
		const { src: userAvatarSrc, isLoading } = useUserAvatarSrc(
			user.userId,
			user.hasAvatar,
		);

		// Handle passthrough fields (firstName, lastName, email) that come from API
		// These fields are not in the schema but are passed through from the API
		const userWithPassthrough = user as OrganizationUserInfo & {
			firstName?: string | null;
			lastName?: string | null;
			email?: string | null;
		};
		const firstName = userWithPassthrough.firstName;
		const lastName = userWithPassthrough.lastName;
		const email = userWithPassthrough.email;

		// Only show initials when not loading and no avatar
		const initials =
			!isLoading && !userAvatarSrc
				? getUserInitials(firstName, lastName)
				: undefined;

		return (
			<div className="flex items-center gap-3">
				<Avatar
					src={userAvatarSrc || undefined}
					initials={initials}
					alt={user.displayName ?? undefined}
					size="md"
				/>
				<div className="whitespace-nowrap">
					<p className="text-sm font-medium text-primary">
						{user.displayName && user.displayName.length > 0 ? (
							user.displayName
						) : firstName &&
							firstName.length > 0 &&
							lastName &&
							lastName.length > 0 ? (
							`${firstName} ${lastName}`
						) : (
							<EmptyValue />
						)}
					</p>
					<p className="text-sm font-medium text-gray-600">
						{email && email.length > 0 ? email : <EmptyValue />}
					</p>
				</div>
			</div>
		);
	}

	const createBasicColumns: ColumnDef<OrganizationUserInfo>[] = [
		{
			id: "userName",
			accessorKey: "userName",
			header: t("admin.users.tabs.table.columns.nameEmail", {
				useFieldInfo: true,
				fieldInfoKeys: ["Organization.Name", "User.Email"],
				fieldInfoJoin: " and ",
			}),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
					className: "text-xs font-semibold, text-gray-500",
				},
			},
			cell: ({ row }) => <UserAvatarCell user={row.original} />,
		},
		{
			id: "role",
			accessorKey: "role",
			header: t("User.Roles", { useFieldInfo: true }),
			enableSorting: true,
			cell: ({ row }) => {
				return (
					<span className="text-sm font-medium text-gray-600">
						{row.original.organizationRoles &&
						row.original.organizationRoles.length > 0 ? (
							row.original.organizationRoles
								?.map((role) => role.name)
								.join(", ")
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "status",
			accessorKey: "status",
			header: t("admin.organizations.drawer.usersTab.orgMemberStatus"),
			enableSorting: true,
			cell: ({ row }) => {
				const statusName = row.original.status.name ?? "";
				const colorMap: Record<
					string,
					"blue" | "success" | "warning" | "gray" | "error"
				> = {
					Pending: "blue",
					Active: "success",
					Suspended: "warning",
					Archived: "gray",
					Removed: "error",
				};
				const color = colorMap[statusName] ?? "gray";
				return statusName ? (
					<BadgeWithDot type="modern" size="md" color={color}>
						{statusName}
					</BadgeWithDot>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "id",
			accessorKey: "id",
			header: t("OrganizationUser.Id", { useFieldInfo: true }),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				return row.original.id ? (
					<div className="inline-flex min-w-0 max-w-full">
						<CopyableId value={row.original.id} />
					</div>
				) : (
					<EmptyValue />
				);
			},
		},
	];

	const userColumns = createBasicColumns;

	return (
		<div>
			<div className="flex items-center justify-between">
				<div className="py-2 px-3 min-w-60 md:min-w-50 lg:min-w-100 ">
					<AvatarLabelGroup
						size="md"
						title={`${t("admin.organizations.drawer.usersTab.orgUsers")}`}
						hideAvatar={true}
					/>
				</div>
			</div>
			{isLoading ? (
				<div className="flex items-center justify-center text-center h-[calc(100vh-16rem)]">
					<LoadingIndicator type="line-simple" size="xl" supportingText />
				</div>
			) : hasError ? (
				<div className="flex flex-col items-center justify-center h-full gap-4">
					<Illustration type="cloud" size="sm">
						<div className="bg-error-500 rounded-full p-3">
							<AlertCircle className="size-6" />
						</div>
					</Illustration>
					<div className="flex flex-col items-center justify-center pt-4">
						<p className="text-gray-900 font-semibold text-md">
							{t("admin.organizations.errors.errorLoadingPage")}
						</p>
						<div className="w-[60%]">
							<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
								{t("admin.organizations.errors.errorLoadingContent")}
							</p>
						</div>
					</div>
					{/* <div className="flex gap-3 justify-center">
						<Button
							color="primary"
							size="md"
							onClick={() => {
								// Reset the error boundary
								queryErrorResetBoundary.reset();
								// Invalidate the route to reload loaders
								router.invalidate();
							}}
						>
							{t("common.actions.retry")}
						</Button>
					</div> */}
				</div>
			) : !users || users.length === 0 ? (
				<div className="flex flex-col items-center justify-center h-full mt-16">
					<Illustration type="cloud" size="sm">
						<User01 className="size-6" />
					</Illustration>
					<div className="flex flex-col items-center justify-center pt-4">
						<p className="text-gray-900 font-semibold text-md">
							{t("admin.organizations.drawer.usersTab.noOrganizationUsers")}
						</p>
						<div className="w-[60%]">
							<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
								{t("admin.organizations.drawer.usersTab.usersWillAppearHere")}
							</p>
						</div>
					</div>
				</div>
			) : (
				<div
					ref={setRootEl}
					style={{
						height: "calc(100vh - 18rem)",
						overflowY: "auto",
						overflowX: "auto",
						position: "relative",
					}}
					className="w-full"
				>
					<div
						style={{
							height: `${virtualizer.getTotalSize()}px`,
							width: "100%",
							position: "relative",
						}}
					>
						<CustomTableWrapper
							data={users}
							columns={userColumns}
							enableSorting={true}
							isFetching={isFetching}
							showCard={false}
							className="text-xs font-semibold text-gray-500"
							virtualizer={virtualizer}
							isVirtualTable={true}
							zebraStriping
						/>
					</div>

					{/* Sentinel element for infinite scroll - observed by IntersectionObserver */}
					{hasNextPage && (
						<div
							ref={loadMoreRef}
							className="h-8 flex items-center justify-center"
							aria-live="polite"
							aria-busy={isFetchingNextPage}
						>
							{isFetchingNextPage && (
								<LoadingIndicator type="line-simple" size="md" supportingText />
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
